import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Portal } from './Portal';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
  shareText: string;
  title?: string;
}

export const ShareModal: React.FC<ShareModalProps> = ({
  isOpen,
  onClose,
  shareUrl,
  shareText,
  title = 'Share this article'
}) => {
  const [copySuccess, setCopySuccess] = useState(false);

  if (!isOpen) return null;

  // Social sharing handlers
  const handleCopyLink = () => {
    // Copy both the share text and the URL to the clipboard
    navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`)
      .then(() => {
        setCopySuccess(true);
        // Don't auto-close the modal so the user can see the success message
      })
      .catch(err => {
        console.error('Failed to copy URL and text: ', err);
      });
  };

  const handleXShare = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}&via=CognitiveBiasLab`;
    window.open(url, '_blank');
  };

  const handleInstagramShare = () => {
    // Instagram doesn't have a direct web sharing API like other platforms,
    // So we will open Instagram's website in a new tab
    window.open('https://www.instagram.com/', '_blank');
  };

  const handleFacebookShare = () => {
    // Facebook doesn't directly support text parameter in its sharer URL, 
    // but we can append the text to the quote parameter
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  const handleWhatsAppShare = () => {
    // Using the WhatsApp web API to pre-fill text and URL
    const url = `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`;
    window.open(url, '_blank');
  };

  const handleLinkedInShare = () => {
    const url = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  const handleTelegramShare = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank');
  };

  return (
    <Portal>
      <div 
        className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
      >
        <div 
          className="bg-white rounded-xl shadow-xl max-w-md w-full animate-fadeIn"
          onClick={(e) => e.stopPropagation()}
          style={{
            animationDuration: '0.2s'
          }}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 id="share-modal-title" className="text-xl font-semibold text-gray-800">{title}</h3>
            <button 
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
              aria-label="Close share modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Modal Content - Share Options */}
          <div className="p-6 space-y-4">
            {/* LinkedIn */}
            <button 
              onClick={handleLinkedInShare}
              className="flex items-center w-full p-3 rounded-lg hover:bg-blue-50 text-left"
            >
              <div className="bg-[#0A66C2] p-2 rounded-lg mr-4">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </div>
              <span className="text-lg">LinkedIn</span>
            </button>
            
            {/* X (formerly Twitter) */}
            <button 
              onClick={handleXShare}
              className="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 text-left"
            >
              <div className="bg-black p-2 rounded-lg mr-4">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </div>
              <span className="text-lg">X</span>
            </button>
            
            {/* Facebook */}
            <button 
              onClick={handleFacebookShare}
              className="flex items-center w-full p-3 rounded-lg hover:bg-blue-50 text-left"
            >
              <div className="bg-[#1877F2] p-2 rounded-lg mr-4">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
              </div>
              <span className="text-lg">Facebook</span>
            </button>
            
            {/* WhatsApp */}
            <button 
              onClick={handleWhatsAppShare}
              className="flex items-center w-full p-3 rounded-lg hover:bg-green-50 text-left"
            >
              <div className="bg-[#25D366] p-2 rounded-lg mr-4">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path fillRule="evenodd" clipRule="evenodd" d="M20.52 3.449C18.24 1.245 15.24 0.05 12.045 0.05C5.463 0.05 0.1 5.413 0.1 11.995C0.1 14.11 0.6 16.17 1.55 18.01L0 24.05L6.2 22.535C7.95 23.395 9.85 23.855 11.795 23.855C18.425 23.855 23.745 18.49 23.745 11.91C23.75 8.795 22.575 5.844 20.505 3.452L20.52 3.449ZM12.05 21.839C10.2 21.839 8.4 21.395 6.85 20.535L6.5 20.334L2.875 21.165L3.69 17.649L3.465 17.284C2.53 15.699 2.029 13.936 2.029 12.116C2.029 6.489 6.432 1.95 12.075 1.95C14.734 1.95 17.215 2.975 19.08 4.765C20.94 6.605 21.925 9.045 21.93 11.66C21.93 17.286 17.445 21.835 12.05 21.835V21.839ZM17.595 14.471C17.3 14.325 15.825 13.61 15.565 13.504C15.3 13.405 15.115 13.354 14.93 13.655C14.75 13.95 14.175 14.625 14.011 14.81C13.845 14.996 13.685 15.021 13.39 14.879C13.1 14.731 12.1 14.39 10.94 13.356C10.035 12.541 9.4 11.556 9.235 11.255C9.07 10.955 9.22 10.804 9.36 10.664C9.495 10.54 9.65 10.35 9.795 10.185C9.945 10.02 9.99 9.895 10.09 9.715C10.19 9.535 10.145 9.369 10.07 9.22C9.99 9.075 9.365 7.595 9.125 7.005C8.895 6.433 8.658 6.509 8.484 6.499C8.319 6.49 8.135 6.486 7.95 6.486C7.765 6.486 7.455 6.565 7.195 6.86C6.935 7.154 6.17 7.869 6.17 9.3C6.17 10.73 7.195 12.104 7.34 12.285C7.49 12.469 9.365 15.316 12.171 16.581C12.981 16.931 13.613 17.152 14.101 17.315C14.901 17.591 15.635 17.554 16.215 17.472C16.865 17.388 18.073 16.754 18.315 16.066C18.555 15.378 18.555 14.786 18.48 14.665C18.405 14.544 18.225 14.471 17.945 14.325L17.595 14.471Z" />
                </svg>
              </div>
              <span className="text-lg">WhatsApp</span>
            </button>
            
            {/* Telegram */}
            <button 
              onClick={handleTelegramShare}
              className="flex items-center w-full p-3 rounded-lg hover:bg-blue-50 text-left"
            >
              <div className="bg-[#0088cc] p-2 rounded-lg mr-4">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.96 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </div>
              <span className="text-lg">Telegram</span>
            </button>
            
            {/* Instagram */}
            <button 
              onClick={handleInstagramShare}
              className="flex items-center w-full p-3 rounded-lg hover:bg-pink-50 text-left"
            >
              <div className="bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] p-2 rounded-lg mr-4">
                <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </div>
              <span className="text-lg">Instagram</span>
            </button>
            
            {/* Copy Link */}
            <div className="relative">
              <button 
                onClick={handleCopyLink}
                className="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 text-left"
                aria-describedby={copySuccess ? "copy-success-message" : undefined}
              >
                <div className="bg-gray-700 p-2 rounded-lg mr-4">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                </div>
                <div>
                  <span className="text-lg">Copy Link</span>
                </div>
              </button>
              
              {copySuccess && (
                <div 
                  id="copy-success-message"
                  className="absolute inset-0 flex items-center justify-center bg-green-50 rounded-lg animate-fadeIn"
                  style={{ animationDuration: '0.2s' }}
                >
                  <div className="flex items-center space-x-2 text-green-600 font-medium">
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span>Link copied!</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}; 